import {
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import React from 'react';
import { KanbanColumn } from './KanbanColumn';

export interface ColumnData {
  id: UniqueIdentifier;
  title: React.ReactNode;
}

export interface ItemData {
  id: UniqueIdentifier;
  columnId: UniqueIdentifier;
  content: React.ReactNode;
}

interface KanbanBoardProps {
  columns: ColumnData[];
  items: ItemData[];
  onDragEnd: (event: DragEndEvent) => void;
  onDragOver?: (event: DragOverEvent) => void;
  onDragStart?: (event: DragStartEvent) => void;
  activeItemId?: UniqueIdentifier | null;
}

export function KanbanBoard({
  columns,
  items,
  onDragEnd,
  onDragOver,
  onDragStart,
  activeItemId,
}: KanbanBoardProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const [internalActiveId, setInternalActiveId] = React.useState<UniqueIdentifier | null>(null);

  const handleDragStart = (event: DragStartEvent) => {
    setInternalActiveId(event.active.id);
    if (onDragStart) onDragStart(event);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setInternalActiveId(null);
    if (onDragEnd) onDragEnd(event);
  };

  const activeId = activeItemId !== undefined ? activeItemId : internalActiveId;
  const activeItem = activeId ? items.find((item) => item.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragOver={onDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="h-full w-full overflow-x-auto">
        <div className="mx-auto flex h-full w-max min-w-full justify-center gap-4 p-4">
          {columns.map((col) => {
            const colItems = items.filter((item) => item.columnId === col.id);
            return <KanbanColumn key={col.id} column={col} items={colItems} />;
          })}
        </div>
      </div>
      <DragOverlay>
        {activeItem ? (
          <div className="opacity-80 scale-105 cursor-grabbing">{activeItem.content}</div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
